import { getLocation, push, replace, RouterLocation } from "connected-react-router";
import { Task } from "redux-saga";
import {
  call,
  cancel,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
  takeLeading,
} from "redux-saga/effects";
import { isActionOf } from "typesafe-actions";

import OrganizationFeature from "@mapmycustomers/shared/enum/OrganizationFeature";
import Version from "@mapmycustomers/shared/enum/Version";
import Organization from "@mapmycustomers/shared/types/Organization";
import { TokenHandoffCodeData } from "@mapmycustomers/shared/types/TokenHandoff";
import UserRef from "@mapmycustomers/shared/types/User";
import ApiError from "@mapmycustomers/shared/util/api/ApiError";
import { isApiError } from "@mapmycustomers/shared/util/assert";

import { hideGlobalCreateActivityModal } from "@app/component/createEditEntity/Activity/store/actions";
import { hideGlobalCreateCompanyModal } from "@app/component/createEditEntity/CreateCompanyModal/store/actions";
import { hideGlobalCreateDealModal } from "@app/component/createEditEntity/CreateDealModal/store/actions";
import { hideGlobalCreatePersonModal } from "@app/component/createEditEntity/CreatePersonModal/store/actions";
import { hideGlobalPostCreationModal } from "@app/component/PostCreationModal/store/actions";
import configService from "@app/config/ConfigService";
import localSettings from "@app/config/LocalSettings";
import { clearChatConversation } from "@app/store/chat";
import { fetchNotificationsUnreadTotal } from "@app/store/notification/actions";
import LoginAppRollout from "@app/types/auth/LoginAppRollout";
import UserDetails from "@app/types/auth/UserDetails";
import Iam from "@app/types/Iam";
import analyticsService, { AnalyticsService } from "@app/util/analytic/AnalyticsService";
import {
  getAuthCallbackUrl,
  getHandoffReturnUrl,
  getLoginAppUrl,
  isMainAppHost,
} from "@app/util/appUrl";
import { getCaptchaToken } from "@app/util/captcha";
import chatSettings from "@app/util/chat/chatSettings";
import { getLocalTimeZone } from "@app/util/dates";
import loggingService from "@app/util/logging";
import { createCodeChallenge, createRandomToken } from "@app/util/pkce";

import Path from "../../enum/Path";
import InvitationInfo from "../../types/auth/InvitationInfo";
import TokenResponse from "../../types/auth/TokenResponse";
import getValidationErrors from "../../util/errorHandling/getValidationErrors";
import hasTrialExpired from "../../util/hasTrialExpired";
import { callApi } from "../api/callApi";
import { initializeApp } from "../app/actions";
import { initializeCumul } from "../cumul/actions";
import { handleError } from "../errors/actions";
import { getMe, getOrganization, getOrganizationId, isCumulEnabled } from "../iam";
import { fetchMe } from "../iam/actions";
import { fetchTeams, fetchUsers } from "../members/actions";

import {
  checkLoginAppRollout,
  checkResetPasswordCodeStatus,
  exchangeAuthorizationCode,
  fetchInvitationInfo,
  fetchUserDetailsSso,
  forgotPassword,
  loggedIn,
  loggedInWithSso,
  login,
  loginAppRolloutChecked,
  logout,
  requestAccountSso,
  resetPassword,
  signInThroughLoginApp,
  signUp,
  signUpMember,
  ssoLogin,
  tryLogin,
  updateToken,
  updateUserStatus,
  userExists,
} from "./actions";
import authService from "./AuthService";
import { isSigningOut } from "./selectors";

export const redirectTo = (url: string) => {
  window.location.href = url;
};

export const replaceLocation = (url: string) => {
  window.location.replace(url);
};

export function* resetAllModals() {
  yield put(hideGlobalCreateActivityModal());
  yield put(hideGlobalCreateCompanyModal());
  yield put(hideGlobalCreateDealModal());
  yield put(hideGlobalCreatePersonModal());
  yield put(hideGlobalPostCreationModal());
}

export function* saveCurrentLocation() {
  // Read the location from the router store rather than window.location: the history is built
  // with basename: basepath (index.tsx), so window.location.pathname would carry a prefix the
  // router location does not.
  const location: RouterLocation<unknown> = yield select(getLocation);
  const me: Iam | undefined = yield select(getMe);
  const ownerId: undefined | UserRef["id"] =
    me?.id ?? (yield call(authService.getStoredSessionUserId));
  yield call(
    authService.saveLocation,
    `${location.pathname}${location.search}${location.hash}`,
    ownerId
  );
}

export function* onLogin() {
  // During a sign-out, login() comes from the sign-out itself (401s on the revoked token, or the
  // route guard once the token is cleared), and the page belongs to the user who is leaving.
  const signingOut: boolean = yield select(isSigningOut);
  if (!signingOut) {
    yield call(saveCurrentLocation);
  }
  yield call(authService.resetSession);
  yield put(tryLogin.success(undefined)); // reset token in the store
  yield put(fetchMe.success(undefined)); // reset "me" data
  yield call(resetAllModals);
  AnalyticsService.traits = {}; // reset traits to avoid sending incorrect orgId/userId combinations
  // Replace rather than push. login() runs under takeEvery, so a 401 storm would otherwise
  // stack one /login entry per dispatch, and Back would walk the user through the detour.
  yield put(replace(Path.LOGIN));
}

export function* onLogout(action: ReturnType<typeof logout>) {
  try {
    const orgId: Organization["id"] | undefined = yield select(getOrganizationId);
    if (orgId) {
      yield callApi("deleteCurrentToken", orgId);
    }
  } catch (error) {
    // Ignore failure, we're half out already. Just log an error and proceed
    loggingService.error("logout: server-side token revocation failed", { error });
  }

  yield call(authService.resetSession);
  yield put(tryLogin.success(undefined)); // reset token in the store
  yield put(fetchMe.success(undefined)); // reset "me" data
  // The chat transcript lives in sessionStorage, and signing out is a same-tab navigation,
  // which does not clear it — so without this the tab keeps the previous rep's conversation,
  // customer names and deal figures included, until the tab itself is closed. Dispatched
  // before we navigate away, while the watcher that wipes storage is still alive.
  yield put(clearChatConversation());
  yield call(chatSettings.resetWindow);
  // sessionStorage survives the same-tab navigation, and a deliberate sign-out returns nobody
  // to a saved page.
  yield call(authService.resetSavedLocation);
  yield call(authService.signOut, action.payload || undefined);
}

export function* onLoggedIn(action: ReturnType<typeof loggedIn>) {
  // assuming token is already in the storage if it's not specified in the action
  if (action.payload.token !== undefined) {
    yield call(authService.setToken, action.payload.token);
    yield put(tryLogin.success(action.payload.token));
  }

  const handoffReturn = localSettings.getHandoffReturn();
  if (handoffReturn) {
    // Taken before the mint: loggedIn is dispatched more than once per sign-in.
    localSettings.resetHandoffReturn();
    try {
      const { code }: TokenHandoffCodeData = yield callApi("createTokenHandoffCode");
      yield call(redirectTo, getHandoffReturnUrl(handoffReturn, code));
      return;
    } catch (error) {
      // An expired session sends the user to sign in again; that sign-in hands over instead.
      if (isApiError(error) && error.status === 401) {
        localSettings.setHandoffReturn(handoffReturn);
      } else {
        yield put(handleError({ error }));
      }
      // stay signed in here rather than strand the user
      loggingService.error("sign-in handoff to a DE failed", { error });
    }
  }

  yield put(initializeApp.request());

  const initializationResult: ReturnType<
    typeof initializeApp.failure | typeof initializeApp.success
  > = yield take([initializeApp.success, initializeApp.failure]);
  if (isActionOf(initializeApp.success, initializationResult)) {
    // move user to a billing page if trial expired
    const organization: Organization | undefined = yield select(getOrganization);
    if (hasTrialExpired(organization)) {
      yield put(push(Path.BILLING));
      // Drop the saved target. This user goes nowhere else until the org pays, and a stale
      // target must not resurface on a later login.
      yield call(authService.resetSavedLocation);
    } else if (!action.payload.keepLocation) {
      // go to saved location only once (most probably right after logging in)
      // do not go there when browser page is refreshed, thus we're resetting it after use
      const location: string = yield call(
        authService.getSavedLocationFor,
        initializationResult.payload.id
      );
      yield put(replace(location));
      yield call(authService.resetSavedLocation);
    }
  }
}

function* saveTokenResponse(tokenResponse: TokenResponse) {
  yield call(authService.setToken, tokenResponse.accessToken);
  localSettings.setSkeletonKeyUsed(tokenResponse?.skeletonKeyUsed ?? false);
  localSettings.setDefaultMapFilterPending(!tokenResponse?.skeletonKeyUsed);

  AnalyticsService.shouldTrack = !tokenResponse?.skeletonKeyUsed;

  if (tokenResponse?.refreshToken) {
    localSettings.setAuthRefreshToken(tokenResponse.refreshToken);
  }
  yield put(tryLogin.success(tokenResponse.accessToken));
}

export function* authorize(username: string, password: string) {
  try {
    const tokenResponse: TokenResponse = yield callApi("authorize", {
      password,
      username,
    });
    yield call(saveTokenResponse, tokenResponse);
  } catch (error) {
    yield put(tryLogin.failure(error));
  }
}

// Any failure keeps the browser on the in-app sign-in. The API registers app2's callback on its
// main address only, so an alias host (www., an old domain) keeps the in-app sign-in too.
function* isInLoginAppRollout() {
  if (
    !configService.getLoginUrl() ||
    !isMainAppHost(configService.getBaseAppUrl(), window.location.host)
  ) {
    return false;
  }
  try {
    // Every sign-in waits on this, so a slow API must not hold up the form.
    const { percent }: LoginAppRollout = yield callApi("fetchLoginAppRollout", {
      noAuth: true,
      timeout: 3000,
    });
    return localSettings.getSignInBucket() < percent;
  } catch (error) {
    loggingService.warning("login app rollout check failed", { error });
    return false;
  }
}

export function* onCheckLoginAppRollout() {
  const usesLoginApp: boolean = yield call(isInLoginAppRollout);
  yield put(loginAppRolloutChecked(usesLoginApp));
  // The login app sent back a failed sign-in, but this browser has left the rollout since. The form
  // then shows the generic error every other fallback to it shows.
  if (
    !usesLoginApp &&
    new URLSearchParams(window.location.search).get("mmc_sign_in_failed") === "1"
  ) {
    yield put(tryLogin.failure(undefined));
  }
}

function* openInAppSignIn() {
  yield put(loginAppRolloutChecked(false));
  yield put(replace(Path.LOGIN));
}

export function* onSignInThroughLoginApp({ payload }: ReturnType<typeof signInThroughLoginApp>) {
  try {
    const loginUrl = configService.getLoginUrl();
    if (!loginUrl) {
      throw new Error("LOGIN_URL is not set");
    }
    const state = createRandomToken();
    const verifier = createRandomToken();
    const codeChallenge: string = yield call(createCodeChallenge, verifier);
    localSettings.setSignInRequest({ state, verifier });
    // replace, so Back from the login app skips /login, which would only forward again
    yield call(
      replaceLocation,
      getLoginAppUrl(loginUrl, {
        codeChallenge,
        failed: payload?.failed,
        locale: localSettings.getLocale(),
        redirectUri: getAuthCallbackUrl(window.location.origin),
        state,
      })
    );
  } catch (error) {
    // fail closed to the in-app sign-in, as the rollout check does
    loggingService.warning("login app sign-in could not start", { error });
    yield call(openInAppSignIn);
  }
}

export function* onExchangeAuthorizationCode({
  payload: search,
}: ReturnType<typeof exchangeAuthorizationCode>) {
  const params = new URLSearchParams(search);
  const code = params.get("code");
  const request = localSettings.getSignInRequest();
  localSettings.resetSignInRequest();
  if (!request && authService.hasToken()) {
    // A signed-in user reopened an old callback URL from the browser's history.
    yield put(replace(Path.ROOT));
    return;
  }
  try {
    if (!code || !request || params.get("state") !== request.state) {
      throw new Error(params.get("error") ?? "no matching sign-in request");
    }
    const tokenResponse: TokenResponse = yield callApi("exchangeAuthorizationCode", {
      code,
      codeVerifier: request.verifier,
      redirectUri: getAuthCallbackUrl(window.location.origin),
    });
    yield call(saveTokenResponse, tokenResponse);
    // replace, so the spent code leaves the address bar and the history
    yield put(replace(Path.ROOT));
  } catch (error) {
    loggingService.warning("sign-in callback failed", { error });
    // The login app keeps no session, so this lands on its form, never back here. The rollout is
    // read again so that a browser taken off it gets the in-app form instead.
    const usesLoginApp: boolean = yield call(isInLoginAppRollout);
    if (usesLoginApp) {
      yield put(signInThroughLoginApp({ failed: true }));
    } else {
      // A generic error: the exchange's own one would blame the password the login app accepted.
      yield put(tryLogin.failure(undefined));
      yield call(openInAppSignIn);
    }
  }
}
export function* onUpdateToken({ payload }: ReturnType<typeof updateToken>) {
  try {
    const tokenResponse: TokenResponse = yield callApi("authorize", {
      grant_type: "refresh_token",
      refresh_token: localSettings.getAuthRefreshToken(),
      scope: JSON.stringify({ anonymize: payload }),
    });
    yield call(authService.setToken, tokenResponse.accessToken);
    if (tokenResponse?.refreshToken) {
      localSettings.setAuthRefreshToken(tokenResponse.refreshToken);
    }
    (payload ? localSettings.setAnonymousFlag : localSettings.removeAnonymousFlag)();
    yield put(fetchMe.request());
    const result: ReturnType<typeof fetchMe.success> = yield take([fetchMe.success]);
    if (isActionOf(fetchMe.success, result)) {
      // Re-fetch members so user/team names (e.g. in the Stack Rank sidebar, which reads from the
      // members store) reflect the new anonymous-mode state without a page refresh. This must run
      // AFTER fetchMe resolves: the server masks user/team names based on the refreshed IAM
      // context, so fetching them before fetchMe.success returns un-anonymized names. App init
      // (store/app/sagas.ts) fetches members after fetchMe for the same reason.
      yield put(fetchUsers.request());
      yield put(fetchTeams.request());
      const isCumulEnabledForOrg: boolean = yield select(isCumulEnabled);
      if (isCumulEnabledForOrg) {
        yield put(initializeCumul.request());
      }
    }
  } catch (error) {
    yield put(tryLogin.failure(error));
  }
}

export function* loginFlow() {
  while (true) {
    const { payload }: ReturnType<typeof tryLogin.request> = yield take(tryLogin.request);
    const task: Task = yield fork(authorize, payload.username, payload.password);
    const action: ReturnType<typeof login | typeof logout | typeof tryLogin.failure> = yield take([
      login,
      logout,
      tryLogin.failure,
    ]);
    if (isActionOf([login, logout], action)) {
      yield cancel(task); // kill authorize task
    }
  }
}

export function* onForgotPassword({ payload: email }: ReturnType<typeof forgotPassword.request>) {
  try {
    yield callApi("requestPasswordReset", email);
    yield put(forgotPassword.success());
  } catch (error) {
    const validationErrors = getValidationErrors(error);
    if (validationErrors.length) {
      yield put(forgotPassword.failure(error));
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onResetPassword({ payload }: ReturnType<typeof resetPassword.request>) {
  try {
    yield callApi("resetPassword", payload.code, payload.password, payload.username);
    yield put(resetPassword.success());
  } catch (error) {
    const validationErrors = getValidationErrors(error);
    if (validationErrors.length) {
      yield put(resetPassword.failure(error));
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onSignUp({ payload }: ReturnType<typeof signUp.request>) {
  try {
    const token: string = yield call(getCaptchaToken, "signup");

    const signUpResult: { organization: Organization; user: UserRef } = yield callApi(
      "signup",
      token,
      payload.fullName,
      payload.email,
      payload.phone,
      payload.password,
      payload.organizationName,
      payload.promoEmails,
      payload.referralSource,
      {
        crm: payload.crm,
        features: {
          [OrganizationFeature.DISALLOW_COLOR]: { enabled: true },
        },
        numFieldReps: payload.numFieldReps.toString(),
        webVersion: Version.V2_BETA,
      },
      { tnc: payload.tnc },
      { timezone: { value: getLocalTimeZone() } }
    );
    yield put(signUp.success(signUpResult));
    localSettings.setSignupCompleted(true);
    analyticsService.completed("Sign Up", {
      email: payload.email,
      fullName: payload.fullName,
      organizationName: payload.organizationName,
      phone: payload.phone,
    });

    // now log-in automatically:
    yield put(tryLogin.request({ password: payload.password, username: payload.email }));
    const action: ReturnType<typeof tryLogin.failure | typeof tryLogin.success> = yield take([
      tryLogin.success,
      tryLogin.failure,
    ]);
    if (isActionOf([tryLogin.failure], action)) {
      // if failed to login from the first attempt, go to the login page to proceed
      yield put(push(Path.ROOT));
    }
    yield put(fetchNotificationsUnreadTotal.request());
  } catch (error) {
    const validationErrors = getValidationErrors(error);
    if (validationErrors.length) {
      yield put(signUp.failure(error));
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onSignUpMember({ payload }: ReturnType<typeof signUpMember.request>) {
  try {
    if (!payload.invitationCode) {
      return; // nothing to do here
    }

    const token: string = yield call(getCaptchaToken, "signup");

    const signUpResult: { organization: Organization; user: UserRef } = yield callApi(
      "acceptInvitation",
      token,
      payload.invitationCode,
      payload.fullName,
      payload.email,
      payload.phone,
      payload.password,
      payload.promoEmails,
      {
        tnc: payload.tnc,
      }
    );
    yield put(signUpMember.success(signUpResult));

    // now log-in automatically:
    yield put(tryLogin.request({ password: payload.password, username: payload.email }));
    const action: ReturnType<typeof tryLogin.failure | typeof tryLogin.success> = yield take([
      tryLogin.success,
      tryLogin.failure,
    ]);
    if (isActionOf([tryLogin.failure], action)) {
      // if failed to login from the first attempt, go to the login page to proceed
      yield put(push(Path.ROOT));
    }
    yield put(fetchNotificationsUnreadTotal.request());
  } catch (error) {
    const validationErrors = getValidationErrors(error);
    if (validationErrors.length) {
      yield put(signUpMember.failure(error));
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onUserExistCheck({ payload }: ReturnType<typeof userExists.request>) {
  try {
    const userExistsResponse: boolean = yield callApi("userExists", payload.username);
    yield put(userExists.success(userExistsResponse));
  } catch (error) {
    const validationErrors = getValidationErrors(error);
    if (validationErrors.length) {
      yield put(userExists.failure(error));
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onFetchInvitationInfo({
  payload,
}: ReturnType<typeof fetchInvitationInfo.request>) {
  try {
    const invitationInfo: InvitationInfo = yield callApi("fetchInvitation", payload);
    yield put(fetchInvitationInfo.success(invitationInfo));
  } catch (error) {
    if (error instanceof ApiError && error?.status === 404) {
      // most probably this invitation code has been used already
      // so let's imitate the expired invitation state
      yield put(
        fetchInvitationInfo.success({
          invitationCodeExpiresAt: new Date(0).toISOString(),
          organizationName: "",
          ownerName: "",
          teamNames: [""],
        })
      );
      return;
    }
    const validationErrors = getValidationErrors(error);
    if (validationErrors.length) {
      yield put(fetchInvitationInfo.failure());
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onLoggedInWithSso({ payload: { sessionId } }: ReturnType<typeof loggedInWithSso>) {
  try {
    const tokenResponse: TokenResponse = yield callApi("authorizeSso", sessionId);
    localSettings.setDefaultMapFilterPending(!tokenResponse.skeletonKeyUsed);
    yield put(loggedIn({ token: tokenResponse.accessToken }));
  } catch (error) {
    yield put(tryLogin.failure(error));
    yield put(push(Path.LOGIN));
  }
}

export function* onRequestAccountSso({
  payload: { callback, sessionId },
}: ReturnType<typeof requestAccountSso.request>) {
  try {
    yield callApi("requestAccountSso", sessionId);
    callback();
  } catch (error) {
    yield put(requestAccountSso.failure(error));
  }
}

export function* onSsoLogin({ payload }: ReturnType<typeof ssoLogin.request>) {
  try {
    const redirectUrl: string = yield callApi("fetchSsoLoginRedirectUri", payload.username);
    yield call(redirectTo, redirectUrl);
    yield put(ssoLogin.success());
  } catch (error) {
    const validationErrors = getValidationErrors(error);
    if (validationErrors.length) {
      yield put(ssoLogin.failure(error));
    } else {
      yield put(handleError({ error }));
    }
  }
}

export function* onFetchUserDetailsSso({
  payload,
}: ReturnType<typeof fetchUserDetailsSso.request>) {
  try {
    const userDetails: UserDetails = yield callApi("fetchUserDetailsSso", payload);
    yield put(fetchUserDetailsSso.success(userDetails));
  } catch (error) {
    yield put(fetchUserDetailsSso.failure(error));
  }
}

export function* onUpdateUserStatus({ payload }: ReturnType<typeof updateUserStatus.request>) {
  try {
    const userDetails: UserDetails = yield callApi("updateUserRequestStatus", payload);
    yield put(updateUserStatus.success({ status: payload.status, userDetails }));
  } catch (error) {
    yield put(updateUserStatus.failure(error));
  }
}

export function* onCheckResetPasswordCodeStatus({
  payload,
}: ReturnType<typeof checkResetPasswordCodeStatus.request>) {
  try {
    const { code, username } = payload;
    yield callApi("checkResetPasswordCodeStatus", code, username);
    yield put(checkResetPasswordCodeStatus.success());
  } catch {
    yield put(checkResetPasswordCodeStatus.failure());
  }
}

export function* authSaga() {
  yield fork(loginFlow);
  yield takeEvery(login, onLogin);
  yield takeEvery(logout, onLogout);
  yield takeEvery(loggedIn, onLoggedIn);
  yield takeLatest(checkLoginAppRollout, onCheckLoginAppRollout);
  yield takeLeading(signInThroughLoginApp, onSignInThroughLoginApp);
  yield takeLeading(exchangeAuthorizationCode, onExchangeAuthorizationCode);
  yield takeLatest(forgotPassword.request, onForgotPassword);
  yield takeLatest(resetPassword.request, onResetPassword);
  yield takeLatest(signUp.request, onSignUp);
  yield takeLatest(signUpMember.request, onSignUpMember);
  yield takeLatest(userExists.request, onUserExistCheck);
  yield takeLatest(fetchInvitationInfo.request, onFetchInvitationInfo);
  yield takeLatest(updateToken, onUpdateToken);
  yield takeEvery(loggedInWithSso, onLoggedInWithSso);
  yield takeEvery(requestAccountSso.request, onRequestAccountSso);
  yield takeLatest(ssoLogin.request, onSsoLogin);
  yield takeLatest(updateUserStatus.request, onUpdateUserStatus);
  yield takeLatest(fetchUserDetailsSso.request, onFetchUserDetailsSso);
  yield takeLatest(checkResetPasswordCodeStatus.request, onCheckResetPasswordCodeStatus);
}
